import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Upload, FileText, TrendingUp, TrendingDown, DollarSign, Percent, PieChart, BarChart3, Activity, ArrowLeft, ArrowUpDown, ArrowUp, ArrowDown, Target, ShieldAlert, AlertTriangle, LogOut } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { PieChart as RechartsPieChart, BarChart, XAxis, YAxis, CartesianGrid, Cell, Pie, Tooltip, Legend, ResponsiveContainer, Bar, LineChart, Line } from "recharts";
import { useNavigate } from 'react-router-dom';
import { IndividualStockScorecard } from '@/components/IndividualStockScorecard';
import { Auth } from '@/components/Auth';
import { supabase } from '@/integrations/supabase/client';
import { cn } from "@/lib/utils";

interface Position {
  ticker: string;
  type: string;
  quantity: number;
  pricePerShare: number;
  totalAmount: number;
  currency: string;
  fxRate: number;
  date: string;
  currentValue?: number;
  costBasis?: number;
  unrealizedPL?: number;
  unrealizedPLPercent?: number;
  avgCost?: number;
}

interface PortfolioSummary {
  totalValue: number;
  totalCostBasis: number;
  totalUnrealizedPL: number;
  totalUnrealizedPLPercent: number;
  positions: Position[];
}

type SortField = keyof Position;
type SortDirection = 'asc' | 'desc';

const RevolutImport = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [user, setUser] = useState<any>(null);
  const [file, setFile] = useState<File | null>(null);
  const [parsing, setParsing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [portfolio, setPortfolio] = useState<PortfolioSummary | null>(null);
  const [sortField, setSortField] = useState<SortField>('ticker');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [selectedPosition, setSelectedPosition] = useState<Position | null>(null);
  const [previewData, setPreviewData] = useState<Position[] | null>(null);
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
    });

    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (user) {
      loadSavedPositions();
    }
  }, [user]);

  const loadSavedPositions = async () => {
    if (!user) return;

    const { data, error } = await supabase
      .from('positions')
      .select('*')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error loading positions:', error);
      return;
    }

    if (data && data.length > 0) {
      // Convert saved positions to portfolio format
      const positions = data.map(p => ({
        ticker: p.ticker,
        type: 'BUY',
        quantity: p.quantity,
        pricePerShare: p.price_per_share || p.buy_price,
        totalAmount: p.total_value || (p.buy_price * p.quantity),
        currency: p.local_currency || 'USD',
        fxRate: 1,
        date: p.created_at,
        currentValue: p.total_value,
        costBasis: p.buy_price * p.quantity,
        unrealizedPL: p.total_value ? (p.total_value - (p.buy_price * p.quantity)) : 0,
        unrealizedPLPercent: p.gain_pct ? p.gain_pct * 100 : 0,
        avgCost: p.buy_price
      }));

      const totalValue = positions.reduce((sum, pos) => sum + (pos.currentValue || 0), 0);
      const totalCostBasis = positions.reduce((sum, pos) => sum + (pos.costBasis || 0), 0);
      const totalUnrealizedPL = totalValue - totalCostBasis;
      const totalUnrealizedPLPercent = totalCostBasis ? (totalUnrealizedPL / totalCostBasis) * 100 : 0;

      setPortfolio({
        totalValue,
        totalCostBasis,
        totalUnrealizedPL,
        totalUnrealizedPLPercent,
        positions
      });
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      if (!selectedFile.name.endsWith('.csv')) {
        toast({
          title: "Invalid File Type",
          description: "Please upload a CSV file from Revolut",
          variant: "destructive"
        });
        return;
      }
      setFile(selectedFile);
    }
  };

  const testParseCSV = async () => {
    if (!file) return;

    setParsing(true);
    setProgress(0);

    try {
      const text = await file.text();
      const allLines = text.split('\n');
      
      // VERIFICATION: Track import statistics
      const verification = {
        totalLines: allLines.length,
        headerLine: 1,
        dataLinesFound: 0,
        blankLinesEncountered: 0,
        linesProcessed: 0,
        linesSkipped: 0,
        transactionsImported: 0,
        uniqueStocks: new Set<string>(),
        skippedReasons: [] as string[],
        firstBlankLineAt: -1
      };
      
      if (allLines.length < 2) {
        throw new Error('CSV file is empty or invalid');
      }

      // Parse ALL lines from line 2 onwards (don't stop at blank lines)
      // Revolut CSVs may have blank lines within data
      const dataLines: string[] = [];
      for (let i = 1; i < allLines.length; i++) {
        const line = allLines[i];
        
        // Track blank lines but DON'T stop
        if (!line.trim()) {
          verification.blankLinesEncountered++;
          if (verification.firstBlankLineAt === -1) {
            verification.firstBlankLineAt = i + 1;
          }
          // Skip blank lines but continue parsing
          continue;
        }
        
        dataLines.push(line);
        verification.dataLinesFound++;
      }

      if (dataLines.length === 0) {
        throw new Error('No data found in CSV file');
      }

      const transactions: Position[] = [];
      const holdings = new Map<string, Position>();

      // Process each transaction
      dataLines.forEach((line, index) => {
        verification.linesProcessed++;
        setProgress(Math.round(((index + 1) / dataLines.length) * 100));
        
        const parts = line.split(',');
        if (parts.length < 8) {
          verification.linesSkipped++;
          verification.skippedReasons.push(`Line ${index + 2}: Insufficient columns (${parts.length} < 8)`);
          return;
        }

        const date = parts[0]?.trim();
        const ticker = parts[1]?.trim();
        const type = parts[2]?.trim();
        const quantityStr = parts[3]?.trim();
        const priceStr = parts[4]?.trim();
        const totalStr = parts[5]?.trim();
        const currency = parts[6]?.trim();
        const fxRateStr = parts[7]?.trim();

        // Skip non-trading transactions (CASH TOP-UP, DIVIDEND)
        if (!ticker || type.includes('CASH') || type.includes('DIVIDEND')) {
          verification.linesSkipped++;
          verification.skippedReasons.push(`Line ${index + 2}: Non-trading transaction (${type})`);
          return;
        }

        // Track this stock
        verification.uniqueStocks.add(ticker);
        verification.transactionsImported++;

        const quantity = parseFloat(quantityStr) || 0;
        const pricePerShare = parseFloat(priceStr.replace(/[A-Z]+\s*/g, '')) || 0;
        const totalAmount = Math.abs(parseFloat(totalStr.replace(/[A-Z]+\s*/g, ''))) || 0;
        const fxRate = parseFloat(fxRateStr) || 1;

        // Calculate position updates
        if (type.includes('BUY')) {
          const existing = holdings.get(ticker);
          if (existing) {
            const newQuantity = existing.quantity + quantity;
            const newCostBasis = (existing.costBasis || 0) + totalAmount;
            existing.quantity = newQuantity;
            existing.costBasis = newCostBasis;
            existing.avgCost = newCostBasis / newQuantity;
          } else {
            holdings.set(ticker, {
              ticker,
              type,
              quantity,
              pricePerShare,
              totalAmount,
              currency,
              fxRate,
              date,
              costBasis: totalAmount,
              avgCost: pricePerShare,
              currentValue: 0,
              unrealizedPL: 0,
              unrealizedPLPercent: 0
            });
          }
        } else if (type.includes('SELL')) {
          const existing = holdings.get(ticker);
          if (existing) {
            const avgCost = existing.avgCost || 0;
            const costReduction = avgCost * quantity;
            existing.quantity -= quantity;
            existing.costBasis = (existing.costBasis || 0) - costReduction;
            
            // Remove if fully sold
            if (existing.quantity <= 0) {
              holdings.delete(ticker);
            }
          }
        }
      });

      // Convert holdings to array and calculate P&L
      // INCLUDE ALL positions even with zero or negative quantity
      const positions = Array.from(holdings.values()).map(pos => {
        // For demo, use last known price as current price
        const currentValue = pos.quantity * pos.pricePerShare;
        const unrealizedPL = currentValue - (pos.costBasis || 0);
        const unrealizedPLPercent = pos.costBasis ? (unrealizedPL / pos.costBasis) * 100 : 0;

        return {
          ...pos,
          currentValue,
          unrealizedPL,
          unrealizedPLPercent
        };
      });

      // Calculate portfolio totals
      const totalValue = positions.reduce((sum, pos) => sum + (pos.currentValue || 0), 0);
      const totalCostBasis = positions.reduce((sum, pos) => sum + (pos.costBasis || 0), 0);
      const totalUnrealizedPL = totalValue - totalCostBasis;
      const totalUnrealizedPLPercent = totalCostBasis ? (totalUnrealizedPL / totalCostBasis) * 100 : 0;

      // SHOW PREVIEW INSTEAD OF SAVING
      setPreviewData(positions);
      setShowPreview(true);
      setParsing(false);
      
      toast({
        title: "Preview Ready",
        description: `Found ${positions.length} positions. Review them below and click "Confirm Import" to save to database.`,
        duration: 8000,
      });
    } catch (error) {
      console.error('Error parsing CSV:', error);
      toast({
        title: "Import Failed",
        description: error instanceof Error ? error.message : "Failed to parse CSV file",
        variant: "destructive"
      });
    } finally {
      setParsing(false);
    }
  };

  const confirmImport = async () => {
    if (!previewData || !user) return;

    setParsing(true);
    try {
      // Save positions to database
      for (const pos of previewData) {
        const { error: insertError } = await supabase
          .from('positions')
          .upsert({
            user_id: user.id,
            ticker: pos.ticker,
            buy_price: pos.avgCost || pos.pricePerShare,
            quantity: pos.quantity,
            local_currency: pos.currency,
            price_per_share: pos.pricePerShare,
            total_value: pos.currentValue,
            gain_pct: pos.unrealizedPLPercent ? pos.unrealizedPLPercent / 100 : 0,
            initial_investment: pos.costBasis,
            stock_name: pos.ticker
          }, {
            onConflict: 'user_id,ticker',
            ignoreDuplicates: false
          });

        if (insertError) {
          console.error('Error saving position:', insertError);
          throw insertError;
        }
      }

      // Calculate portfolio totals
      const totalValue = previewData.reduce((sum, pos) => sum + (pos.currentValue || 0), 0);
      const totalCostBasis = previewData.reduce((sum, pos) => sum + (pos.costBasis || 0), 0);
      const totalUnrealizedPL = totalValue - totalCostBasis;
      const totalUnrealizedPLPercent = totalCostBasis ? (totalUnrealizedPL / totalCostBasis) * 100 : 0;

      setPortfolio({
        totalValue,
        totalCostBasis,
        totalUnrealizedPL,
        totalUnrealizedPLPercent,
        positions: previewData
      });

      toast({
        title: "Import Successful",
        description: `Saved ${previewData.length} positions to database. Generating report...`,
      });

      // Send portfolio report email
      supabase.functions.invoke('send-portfolio-report', {
        body: {
          positions: previewData.map(pos => ({
            ticker: pos.ticker,
            stock_name: pos.ticker,
            quantity: pos.quantity,
            buy_price: pos.avgCost || pos.pricePerShare,
            price_per_share: pos.pricePerShare,
            total_value: pos.currentValue || 0,
            gain_pct: pos.unrealizedPLPercent || 0,
            added_value_chf: pos.unrealizedPL || 0,
            local_currency: pos.currency
          })),
          userEmail: user.email,
          importType: 'Revolut'
        }
      }).then(response => {
        if (response.error) {
          console.error('Failed to send portfolio report:', response.error);
        } else {
          console.log('Portfolio report sent successfully');
          toast({
            title: "Report Sent",
            description: "Portfolio analysis report has been sent to your email",
          });
        }
      });
    } catch (error) {
      console.error('Error parsing CSV:', error);
      toast({
        title: "Import Failed",
        description: error instanceof Error ? error.message : "Failed to parse CSV file",
        variant: "destructive"
      });
    } finally {
      setParsing(false);
    }
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const sortedPositions = portfolio?.positions.slice().sort((a, b) => {
    const aVal = a[sortField];
    const bVal = b[sortField];
    const multiplier = sortDirection === 'asc' ? 1 : -1;

    if (typeof aVal === 'number' && typeof bVal === 'number') {
      return (aVal - bVal) * multiplier;
    }
    if (typeof aVal === 'string' && typeof bVal === 'string') {
      return aVal.localeCompare(bVal) * multiplier;
    }
    return 0;
  }) || [];

  const getCurrencySymbol = (currency?: string): string => {
    const symbols: { [key: string]: string } = {
      'USD': '$',
      'EUR': '€',
      'GBP': '£',
      'CHF': 'CHF',
    };
    return symbols[currency || ''] || currency || '';
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate('/');
  };

  const formatCurrency = (value: number, currency?: string): string => {
    const symbol = getCurrencySymbol(currency);
    return `${symbol}${value.toFixed(2)}`;
  };

  const getAlertLevel = (gainPct: number) => {
    if (gainPct >= 45) return { color: 'text-red-500 bg-red-50', label: 'Take Profit', icon: Target };
    if (gainPct >= 30) return { color: 'text-orange-500 bg-orange-50', label: 'Profit Alert', icon: AlertTriangle };
    if (gainPct >= 15) return { color: 'text-yellow-500 bg-yellow-50', label: 'Profit Zone', icon: TrendingUp };
    if (gainPct < -5) return { color: 'text-destructive bg-red-50', label: 'Stop Loss', icon: ShieldAlert };
    return { color: 'text-muted-foreground bg-muted', label: 'Hold', icon: Activity };
  };

  // Calculate sector breakdown (by currency for Revolut)
  const currencyBreakdown = sortedPositions.reduce((acc, pos) => {
    const curr = pos.currency || 'Unknown';
    if (!acc[curr]) {
      acc[curr] = { value: 0, count: 0 };
    }
    acc[curr].value += pos.currentValue || 0;
    acc[curr].count += 1;
    return acc;
  }, {} as Record<string, { value: number; count: number }>);

  const currencyChartData = Object.entries(currencyBreakdown).map(([name, data]) => ({
    name,
    value: data.value,
    count: data.count
  }));

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82CA9D'];

  if (!user) {
    return <Auth />;
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8 max-w-7xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/')}
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-4xl font-bold text-foreground flex items-center gap-3">
                <FileText className="h-10 w-10 text-primary" />
                Revolut Portfolio Analysis
              </h1>
              <p className="text-muted-foreground mt-1">
                Import and analyze your Revolut trading history (Private to you)
              </p>
            </div>
          </div>
          <Button variant="ghost" size="lg" onClick={handleSignOut}>
            <LogOut className="mr-2 h-5 w-5" />
            Sign Out
          </Button>
        </div>

        {/* Trading Guidelines */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Target className="h-5 w-5 text-primary" />
              Swing Trading Guidelines
            </CardTitle>
            <CardDescription>Professional profit-taking and loss protection strategies</CardDescription>
          </CardHeader>
          <CardContent>
            <Accordion type="single" collapsible className="w-full">
              <AccordionItem value="profit-alerts">
                <AccordionTrigger className="text-lg font-semibold">
                  📈 Profit-Taking Alerts (Added Value Gain)
                </AccordionTrigger>
                <AccordionContent className="space-y-4 text-base">
                  <div>
                    <h4 className="font-semibold text-primary mb-2">Reward-to-Risk Ratio:</h4>
                    <p className="text-muted-foreground mb-1">Many swing traders aim for at least 3:1 reward:risk.</p>
                    <p className="text-sm italic">Example: If risking 5% (stop-loss), set profit target at ~15% above entry.</p>
                  </div>
                  
                  <div>
                    <h4 className="font-semibold text-primary mb-2">Technical Levels:</h4>
                    <p className="text-muted-foreground mb-1">Place profit alerts near resistance zones or prior swing highs.</p>
                    <p className="text-sm italic">If the stock has strong momentum, targets can extend to 4:1 or 6:1 reward:risk.</p>
                  </div>
                  
                  <div>
                    <h4 className="font-semibold text-primary mb-2">Partial Profit-Taking:</h4>
                    <p className="text-muted-foreground mb-1">Sell part of the position at the first target (e.g., 2:1) and let the rest run toward higher levels.</p>
                    <p className="text-sm italic">This locks in gains while keeping upside potential.</p>
                  </div>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="loss-protection">
                <AccordionTrigger className="text-lg font-semibold">
                  📉 Loss Protection Alerts (Stop-Loss)
                </AccordionTrigger>
                <AccordionContent className="space-y-4 text-base">
                  <div>
                    <h4 className="font-semibold text-destructive mb-2">Percentage-Based Stops:</h4>
                    <p className="text-muted-foreground mb-1">Commonly 5–10% below entry price for swing trades.</p>
                    <p className="text-sm italic">Example: Buy at $100, stop-loss at $95 (5% risk).</p>
                  </div>
                  
                  <div>
                    <h4 className="font-semibold text-destructive mb-2">Technical Stops:</h4>
                    <p className="text-muted-foreground mb-1">Place stop-loss just below the most recent swing low in an uptrend, or above the swing high in a short trade.</p>
                    <p className="text-sm italic">This aligns with natural support/resistance levels.</p>
                  </div>
                  
                  <div>
                    <h4 className="font-semibold text-destructive mb-2">Dynamic Stops with Moving Averages:</h4>
                    <p className="text-muted-foreground mb-1">Use the 20-day or 50-day moving average as a trailing stop.</p>
                    <p className="text-sm italic">If price closes below the moving average, exit the trade.</p>
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </CardContent>
        </Card>

        {/* Upload Section */}
        {!portfolio && (
          <Card className="mb-8">
            <CardHeader>
              <CardTitle>Upload Revolut CSV</CardTitle>
              <CardDescription>
                Export your trading history from Revolut and upload the CSV file here
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-4">
                <Button
                  variant="outline"
                  onClick={() => document.getElementById('csv-upload')?.click()}
                  disabled={parsing}
                >
                  <Upload className="mr-2 h-4 w-4" />
                  Select CSV File
                </Button>
                <input
                  id="csv-upload"
                  type="file"
                  accept=".csv"
                  className="hidden"
                  onChange={handleFileSelect}
                />
                {file && (
                  <span className="text-sm text-muted-foreground">
                    {file.name}
                  </span>
                )}
              </div>

              {file && !showPreview && (
                <Button onClick={testParseCSV} disabled={parsing} className="w-full">
                  {parsing ? (
                    <>
                      <Activity className="mr-2 h-4 w-4 animate-spin" />
                      Parsing...
                    </>
                  ) : (
                    <>
                      <Upload className="mr-2 h-4 w-4" />
                      Test & Preview CSV Data
                    </>
                  )}
                </Button>
              )}

              {showPreview && previewData && (
                <div className="space-y-4">
                  <Card className="bg-primary/5 border-primary">
                    <CardHeader>
                      <CardTitle className="text-xl">Preview: {previewData.length} Positions Found</CardTitle>
                      <CardDescription>
                        Review the parsed data below. Click "Confirm Import" to save to database.
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="max-h-96 overflow-auto">
                        <table className="w-full text-sm">
                          <thead className="sticky top-0 bg-background">
                            <tr className="border-b">
                              <th className="text-left p-2">Ticker</th>
                              <th className="text-right p-2">Qty</th>
                              <th className="text-right p-2">Avg Cost</th>
                              <th className="text-right p-2">Current</th>
                              <th className="text-right p-2">Value</th>
                              <th className="text-right p-2">P&L %</th>
                            </tr>
                          </thead>
                          <tbody>
                            {previewData.map((pos, idx) => {
                              const getQuantityColor = (qty: number) => {
                                if (qty > 0) return "bg-green-50 hover:bg-green-100";
                                if (qty === 0) return "bg-yellow-50 hover:bg-yellow-100";
                                return "bg-red-50 hover:bg-red-100";
                              };
                              
                              return (
                                <tr 
                                  key={idx} 
                                  className={cn("border-b cursor-pointer transition-all", getQuantityColor(pos.quantity))}
                                  onClick={() => setSelectedPosition({
                                    ticker: pos.ticker,
                                    description: pos.ticker,
                                    sector: 'Unknown',
                                    quantity: pos.quantity,
                                    avgCost: pos.avgCost || pos.pricePerShare,
                                    currentPrice: pos.pricePerShare,
                                    marketValue: pos.currentValue || 0,
                                    costBasis: pos.costBasis || 0,
                                    unrealizedPL: pos.unrealizedPL || 0,
                                    unrealizedPLPercent: pos.unrealizedPLPercent || 0,
                                    currency: pos.currency
                                  } as any)}
                                >
                                  <td className="p-2 font-medium">{pos.ticker}</td>
                                  <td className="p-2 text-right font-semibold">{pos.quantity.toFixed(2)}</td>
                                  <td className="p-2 text-right">{getCurrencySymbol(pos.currency)}{pos.avgCost?.toFixed(2)}</td>
                                  <td className="p-2 text-right">{getCurrencySymbol(pos.currency)}{pos.pricePerShare.toFixed(2)}</td>
                                  <td className="p-2 text-right">{getCurrencySymbol(pos.currency)}{pos.currentValue?.toFixed(2)}</td>
                                  <td className={cn(
                                    "p-2 text-right font-medium",
                                    (pos.unrealizedPLPercent || 0) >= 0 ? "text-green-500" : "text-red-500"
                                  )}>
                                    {(pos.unrealizedPLPercent || 0).toFixed(2)}%
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </CardContent>
                  </Card>
                  
                  <div className="flex gap-2">
                    <Button onClick={confirmImport} disabled={parsing} className="flex-1">
                      {parsing ? (
                        <>
                          <Activity className="mr-2 h-4 w-4 animate-spin" />
                          Saving...
                        </>
                      ) : (
                        <>
                          <Upload className="mr-2 h-4 w-4" />
                          Confirm Import to Database
                        </>
                      )}
                    </Button>
                    <Button onClick={() => {
                      setShowPreview(false);
                      setPreviewData(null);
                    }} variant="outline">
                      Cancel
                    </Button>
                  </div>
                </div>
              )}

              {parsing && (
                <div className="space-y-2">
                  <Progress value={progress} />
                  <p className="text-sm text-center text-muted-foreground">
                    Processing: {progress}%
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Portfolio Analysis */}
        {portfolio && (
          <Tabs defaultValue="overview" className="space-y-6">
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="positions">All Positions</TabsTrigger>
              <TabsTrigger value="alerts">Alerts</TabsTrigger>
              <TabsTrigger value="charts">Charts</TabsTrigger>
            </TabsList>

            {/* Overview Tab */}
            <TabsContent value="overview" className="space-y-6">
              {/* Key Metrics */}
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Total Value</CardTitle>
                    <DollarSign className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{formatCurrency(portfolio.totalValue, 'EUR')}</div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Cost Basis</CardTitle>
                    <TrendingDown className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{formatCurrency(portfolio.totalCostBasis, 'EUR')}</div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Unrealized P&L</CardTitle>
                    {portfolio.totalUnrealizedPL >= 0 ? (
                      <TrendingUp className="h-4 w-4 text-green-500" />
                    ) : (
                      <TrendingDown className="h-4 w-4 text-red-500" />
                    )}
                  </CardHeader>
                  <CardContent>
                    <div className={cn(
                      "text-2xl font-bold",
                      portfolio.totalUnrealizedPL >= 0 ? "text-green-500" : "text-red-500"
                    )}>
                      {formatCurrency(portfolio.totalUnrealizedPL, 'EUR')}
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Return %</CardTitle>
                    <Percent className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className={cn(
                      "text-2xl font-bold",
                      portfolio.totalUnrealizedPLPercent >= 0 ? "text-green-500" : "text-red-500"
                    )}>
                      {portfolio.totalUnrealizedPLPercent.toFixed(2)}%
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Quick Stats */}
              <Card>
                <CardHeader>
                  <CardTitle>Portfolio Summary</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-muted-foreground">Total Positions:</span>
                      <span className="font-semibold">{portfolio.positions.length}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-muted-foreground">Winning Positions:</span>
                      <span className="font-semibold text-green-500">
                        {portfolio.positions.filter(p => (p.unrealizedPLPercent || 0) > 0).length}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-muted-foreground">Losing Positions:</span>
                      <span className="font-semibold text-red-500">
                        {portfolio.positions.filter(p => (p.unrealizedPLPercent || 0) < 0).length}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* All Positions Tab */}
            <TabsContent value="positions" className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>All Positions ({sortedPositions.length})</CardTitle>
                  <CardDescription>Click on any row to see detailed trading scorecard with profit targets and stop-loss levels</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left p-2">Alert</th>
                          <th className="text-left p-2 cursor-pointer hover:bg-muted" onClick={() => handleSort('ticker')}>
                            <div className="flex items-center gap-1">
                              Ticker
                              <ArrowUpDown className="h-4 w-4" />
                            </div>
                          </th>
                          <th className="text-center p-2">Symbol</th>
                          <th className="text-right p-2 cursor-pointer hover:bg-muted" onClick={() => handleSort('quantity')}>
                            <div className="flex items-center justify-end gap-1">
                              Quantity
                              <ArrowUpDown className="h-4 w-4" />
                            </div>
                          </th>
                          <th className="text-right p-2 cursor-pointer hover:bg-muted" onClick={() => handleSort('avgCost')}>
                            <div className="flex items-center justify-end gap-1">
                              Avg Cost
                              <ArrowUpDown className="h-4 w-4" />
                            </div>
                          </th>
                          <th className="text-right p-2 cursor-pointer hover:bg-muted" onClick={() => handleSort('pricePerShare')}>
                            <div className="flex items-center justify-end gap-1">
                              Current Price
                              <ArrowUpDown className="h-4 w-4" />
                            </div>
                          </th>
                          <th className="text-right p-2 cursor-pointer hover:bg-muted" onClick={() => handleSort('currentValue')}>
                            <div className="flex items-center justify-end gap-1">
                              Market Value
                              <ArrowUpDown className="h-4 w-4" />
                            </div>
                          </th>
                          <th className="text-right p-2">Next Target</th>
                          <th className="text-right p-2">Stop Loss (5%)</th>
                          <th className="text-right p-2 cursor-pointer hover:bg-muted" onClick={() => handleSort('unrealizedPL')}>
                            <div className="flex items-center justify-end gap-1">
                              P&L
                              <ArrowUpDown className="h-4 w-4" />
                            </div>
                          </th>
                          <th className="text-right p-2 cursor-pointer hover:bg-muted" onClick={() => handleSort('unrealizedPLPercent')}>
                            <div className="flex items-center justify-end gap-1">
                              P&L %
                              <ArrowUpDown className="h-4 w-4" />
                            </div>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {sortedPositions.map((pos, idx) => {
                          const gainPct = pos.unrealizedPLPercent || 0;
                          const alertColor = gainPct >= 45 ? 'bg-red-500/10' :
                                           gainPct >= 30 ? 'bg-orange-500/10' :
                                           gainPct >= 15 ? 'bg-yellow-500/10' :
                                           gainPct < -5 ? 'bg-red-600/10' : '';
                          
                          const avgCost = pos.avgCost || pos.pricePerShare;
                          const profitTargets = [
                            { ratio: '2:1', price: avgCost * 1.10 },
                            { ratio: '3:1', price: avgCost * 1.15 },
                            { ratio: '4:1', price: avgCost * 1.20 },
                            { ratio: '6:1', price: avgCost * 1.30 },
                          ];
                          const nextTarget = profitTargets.find(t => pos.pricePerShare < t.price);
                          const stopLoss = avgCost * 0.95;
                          
                          return (
                            <tr 
                              key={idx} 
                              className={cn(
                                "border-b hover:bg-muted/80 cursor-pointer transition-all",
                                alertColor
                              )}
                              onClick={() => setSelectedPosition(pos)}
                            >
                              <td className="p-2">
                                <div className={cn(
                                  "inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-bold whitespace-nowrap",
                                  gainPct >= 45 ? "bg-red-500 text-white animate-pulse" :
                                  gainPct >= 30 ? "bg-orange-500 text-white" :
                                  gainPct >= 15 ? "bg-yellow-500 text-white" :
                                  gainPct < -5 ? "bg-red-600 text-white" :
                                  "bg-gray-400 text-white"
                                )}>
                                  {gainPct >= 45 ? '🎯 SELL' :
                                   gainPct >= 30 ? '⚡' :
                                   gainPct >= 15 ? '💰' :
                                   gainPct < -5 ? '🛑' : 'HOLD'}
                                </div>
                              </td>
                              <td className="p-2 font-semibold">{pos.ticker}</td>
                              <td className="text-center p-2 font-medium text-lg">{pos.currency}</td>
                              <td className="p-2 text-right">{pos.quantity.toFixed(2)}</td>
                              <td className="p-2 text-right">{formatCurrency(avgCost, pos.currency)}</td>
                              <td className="p-2 text-right font-bold">{formatCurrency(pos.pricePerShare, pos.currency)}</td>
                              <td className="p-2 text-right font-semibold">{formatCurrency(pos.currentValue || 0, pos.currency)}</td>
                              <td className="text-right p-2">
                                {nextTarget ? (
                                  <div>
                                    <div className="font-bold text-green-600 text-sm">{formatCurrency(nextTarget.price, pos.currency)}</div>
                                    <div className="text-xs text-muted-foreground">{nextTarget.ratio}</div>
                                  </div>
                                ) : (
                                  <span className="text-xs text-green-600">Target reached ✓</span>
                                )}
                              </td>
                              <td className="text-right p-2">
                                <div className="font-bold text-red-600 text-sm">{formatCurrency(stopLoss, pos.currency)}</div>
                                <div className="text-xs text-muted-foreground">-5%</div>
                              </td>
                              <td className={cn(
                                "p-2 text-right font-semibold",
                                (pos.unrealizedPL || 0) >= 0 ? "text-green-600" : "text-red-600"
                              )}>
                                {(pos.unrealizedPL || 0) >= 0 && '+'}
                                {formatCurrency(pos.unrealizedPL || 0, pos.currency)}
                              </td>
                              <td className={cn(
                                "p-2 text-right font-semibold",
                                gainPct >= 0 ? "text-green-600" : "text-red-600"
                              )}>
                                {gainPct >= 0 && '+'}
                                {gainPct.toFixed(2)}%
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  
                  {/* Legend */}
                  <div className="mt-4 p-4 bg-muted/50 rounded-lg">
                    <div className="space-y-3">
                      <div className="text-sm font-semibold">Alert Legend:</div>
                      <div className="flex items-center gap-6 text-sm flex-wrap">
                        <div className="flex items-center gap-2">
                          <div className="w-4 h-4 bg-red-500 rounded"></div>
                          <span className="text-xs">≥45% - Take Profit Now</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="w-4 h-4 bg-orange-500 rounded"></div>
                          <span className="text-xs">≥30% - Profit Zone</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="w-4 h-4 bg-yellow-500 rounded"></div>
                          <span className="text-xs">≥15% - Profit Alert</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="w-4 h-4 bg-red-600 rounded"></div>
                          <span className="text-xs">&lt;-5% - Stop Loss Alert</span>
                        </div>
                      </div>
                      <div className="text-xs text-muted-foreground mt-2">
                        <strong>Click any row</strong> to see detailed trading scorecard with profit targets and stop-loss recommendations
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Alerts Tab */}
            <TabsContent value="alerts" className="space-y-6">
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                {['Take Profit', 'Profit Alert', 'Profit Zone', 'Stop Loss'].map((alertType) => {
                  const positions = sortedPositions.filter(p => {
                    const gainPct = p.unrealizedPLPercent || 0;
                    if (alertType === 'Take Profit') return gainPct >= 45;
                    if (alertType === 'Profit Alert') return gainPct >= 30 && gainPct < 45;
                    if (alertType === 'Profit Zone') return gainPct >= 15 && gainPct < 30;
                    if (alertType === 'Stop Loss') return gainPct < -5;
                    return false;
                  });

                  return (
                    <Card key={alertType}>
                      <CardHeader>
                        <CardTitle className="text-lg">{alertType}</CardTitle>
                        <CardDescription>{positions.length} positions</CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-2">
                          {positions.map((pos, idx) => (
                            <div 
                              key={idx}
                              className="flex justify-between items-center p-2 rounded hover:bg-muted cursor-pointer"
                              onClick={() => setSelectedPosition(pos)}
                            >
                              <span className="font-semibold">{pos.ticker}</span>
                              <span className={cn(
                                "font-bold",
                                (pos.unrealizedPLPercent || 0) >= 0 ? "text-green-500" : "text-red-500"
                              )}>
                                {(pos.unrealizedPLPercent || 0).toFixed(2)}%
                              </span>
                            </div>
                          ))}
                          {positions.length === 0 && (
                            <p className="text-sm text-muted-foreground text-center py-4">
                              No positions in this category
                            </p>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </TabsContent>

            {/* Charts Tab */}
            <TabsContent value="charts" className="space-y-6">
              <div className="grid gap-6 md:grid-cols-2">
                {/* Currency Breakdown */}
                <Card>
                  <CardHeader>
                    <CardTitle>Portfolio by Currency</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={300}>
                      <RechartsPieChart>
                        <Pie
                          data={currencyChartData}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={(entry) => `${entry.name}: ${entry.count}`}
                          outerRadius={80}
                          fill="#8884d8"
                          dataKey="value"
                        >
                          {currencyChartData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(value: number) => formatCurrency(value, 'EUR')} />
                        <Legend />
                      </RechartsPieChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>

                {/* P&L Distribution */}
                <Card>
                  <CardHeader>
                    <CardTitle>P&L Distribution</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={sortedPositions.slice(0, 10)}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="ticker" />
                        <YAxis />
                        <Tooltip formatter={(value: number) => `${value.toFixed(2)}%`} />
                        <Bar dataKey="unrealizedPLPercent" fill="#8884d8">
                          {sortedPositions.slice(0, 10).map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={(entry.unrealizedPLPercent || 0) >= 0 ? '#10b981' : '#ef4444'} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
          </Tabs>
        )}

        {/* Action Scorecards - Quick Decision Guide */}
        {portfolio && sortedPositions.length > 0 && (
          <div className="mt-12 space-y-6">
            <h2 className="text-2xl font-bold flex items-center gap-2">
              <Target className="h-6 w-6 text-primary" />
              Action Scorecards - Quick Decision Guide
            </h2>

            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-4 gap-4">
              {/* Take Profit Now */}
              {(() => {
                const takeProfitPositions = sortedPositions.filter(p => (p.unrealizedPLPercent || 0) >= 45);
                return takeProfitPositions.length > 0 && (
                  <Card className="border-2 border-red-500 bg-red-500/5">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <div className="h-3 w-3 bg-red-500 rounded-full animate-pulse"></div>
                        Take Profit Now ({takeProfitPositions.length})
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {takeProfitPositions.map((pos) => (
                        <div 
                          key={pos.ticker} 
                          className="p-3 bg-background rounded-lg border border-red-200 cursor-pointer hover:bg-red-50 transition-colors"
                          onClick={() => setSelectedPosition(pos)}
                        >
                          <div className="flex items-center justify-between mb-2">
                            <span className="font-bold">{pos.ticker}</span>
                            <Badge className="bg-red-500 text-white">+{(pos.unrealizedPLPercent || 0).toFixed(2)}%</Badge>
                          </div>
                          <div className="space-y-1 text-sm">
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Sell 50%:</span>
                              <span className="font-semibold">{Math.floor(pos.quantity * 0.5)} shares</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Lock in:</span>
                              <span className="font-semibold text-green-600">
                                {formatCurrency(Math.floor(pos.quantity * 0.5) * pos.pricePerShare, pos.currency)}
                              </span>
                            </div>
                          </div>
                          <div className="mt-2 pt-2 border-t">
                            <p className="text-xs font-medium text-red-600">🎯 Click for full scorecard</p>
                          </div>
                        </div>
                      ))}
                    </CardContent>
                  </Card>
                );
              })()}

              {/* Profit Zone */}
              {(() => {
                const profitZonePositions = sortedPositions.filter(p => {
                  const pct = p.unrealizedPLPercent || 0;
                  return pct >= 30 && pct < 45;
                });
                return profitZonePositions.length > 0 && (
                  <Card className="border-2 border-orange-500 bg-orange-500/5">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <AlertTriangle className="h-5 w-5 text-orange-500" />
                        Profit Zone ({profitZonePositions.length})
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {profitZonePositions.map((pos) => (
                        <div 
                          key={pos.ticker}
                          className="p-3 bg-background rounded-lg border border-orange-200 cursor-pointer hover:bg-orange-50 transition-colors"
                          onClick={() => setSelectedPosition(pos)}
                        >
                          <div className="flex items-center justify-between mb-2">
                            <span className="font-bold">{pos.ticker}</span>
                            <Badge className="bg-orange-500 text-white">+{(pos.unrealizedPLPercent || 0).toFixed(2)}%</Badge>
                          </div>
                          <div className="space-y-1 text-sm">
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Consider:</span>
                              <span className="font-semibold">Partial profit</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Current:</span>
                              <span className="font-semibold">{formatCurrency(pos.pricePerShare, pos.currency)}</span>
                            </div>
                          </div>
                          <div className="mt-2 pt-2 border-t">
                            <p className="text-xs font-medium text-orange-600">⚡ Click for targets</p>
                          </div>
                        </div>
                      ))}
                    </CardContent>
                  </Card>
                );
              })()}

              {/* Profit Alert */}
              {(() => {
                const profitAlertPositions = sortedPositions.filter(p => {
                  const pct = p.unrealizedPLPercent || 0;
                  return pct >= 15 && pct < 30;
                });
                return profitAlertPositions.length > 0 && (
                  <Card className="border-2 border-yellow-500 bg-yellow-500/5">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <TrendingUp className="h-5 w-5 text-yellow-600" />
                        Profit Alert ({profitAlertPositions.length})
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {profitAlertPositions.map((pos) => (
                        <div 
                          key={pos.ticker}
                          className="p-3 bg-background rounded-lg border border-yellow-200 cursor-pointer hover:bg-yellow-50 transition-colors"
                          onClick={() => setSelectedPosition(pos)}
                        >
                          <div className="flex items-center justify-between mb-2">
                            <span className="font-bold">{pos.ticker}</span>
                            <Badge className="bg-yellow-500 text-white">+{(pos.unrealizedPLPercent || 0).toFixed(2)}%</Badge>
                          </div>
                          <div className="space-y-1 text-sm">
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Status:</span>
                              <span className="font-semibold">Monitor closely</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">P&L:</span>
                              <span className="font-semibold text-green-600">
                                {formatCurrency(pos.unrealizedPL || 0, pos.currency)}
                              </span>
                            </div>
                          </div>
                          <div className="mt-2 pt-2 border-t">
                            <p className="text-xs font-medium text-yellow-600">💰 Click for details</p>
                          </div>
                        </div>
                      ))}
                    </CardContent>
                  </Card>
                );
              })()}

              {/* Stop Loss Alert */}
              {(() => {
                const stopLossPositions = sortedPositions.filter(p => (p.unrealizedPLPercent || 0) < -5);
                return stopLossPositions.length > 0 && (
                  <Card className="border-2 border-red-600 bg-red-600/5">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <ShieldAlert className="h-5 w-5 text-red-600" />
                        Stop Loss Alert ({stopLossPositions.length})
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {stopLossPositions.map((pos) => (
                        <div 
                          key={pos.ticker}
                          className="p-3 bg-background rounded-lg border border-red-300 cursor-pointer hover:bg-red-50 transition-colors"
                          onClick={() => setSelectedPosition(pos)}
                        >
                          <div className="flex items-center justify-between mb-2">
                            <span className="font-bold">{pos.ticker}</span>
                            <Badge className="bg-red-600 text-white">{(pos.unrealizedPLPercent || 0).toFixed(2)}%</Badge>
                          </div>
                          <div className="space-y-1 text-sm">
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Action:</span>
                              <span className="font-semibold text-red-600">Consider exit</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Loss:</span>
                              <span className="font-semibold text-red-600">
                                {formatCurrency(pos.unrealizedPL || 0, pos.currency)}
                              </span>
                            </div>
                          </div>
                          <div className="mt-2 pt-2 border-t">
                            <p className="text-xs font-medium text-red-600">🛑 Click for stop levels</p>
                          </div>
                        </div>
                      ))}
                    </CardContent>
                  </Card>
                );
              })()}
            </div>
          </div>
        )}

        {/* Reset Button */}
        {portfolio && (
          <div className="flex justify-center mt-8">
            <Button
              variant="outline"
              onClick={() => {
                setPortfolio(null);
                setFile(null);
              }}
            >
              <Upload className="mr-2 h-4 w-4" />
              Import Another File
            </Button>
          </div>
        )}
      </div>

      {/* Individual Stock Scorecard Dialog */}
      {selectedPosition && (
        <IndividualStockScorecard
          position={{
            ticker: selectedPosition.ticker,
            description: `${selectedPosition.type}`,
            sector: selectedPosition.currency,
            quantity: selectedPosition.quantity,
            marketValue: selectedPosition.currentValue || 0,
            costBasis: selectedPosition.costBasis || 0,
            unrealizedPL: selectedPosition.unrealizedPL || 0,
            unrealizedPLPercent: selectedPosition.unrealizedPLPercent || 0,
            currentPrice: selectedPosition.pricePerShare,
            avgCost: selectedPosition.avgCost || 0,
            currency: selectedPosition.currency
          }}
          open={!!selectedPosition}
          onClose={() => setSelectedPosition(null)}
        />
      )}
    </div>
  );
};

export default RevolutImport;
